// Cartes PVC 86 × 54 mm — finition premium obligatoire : cadre foil métallisé à coins
// renforcés, ornement à losange, symbole NFC, et verso complet.
import { useId } from "react";
import { Globe, MessageSquare, Star, Instagram, Music2 } from "lucide-react";
import { patternUri } from "../lib/patterns";
import { mix } from "../lib/colors";
import { SITE_URL } from "../config/site";

export function NfcWaves({ color = "currentColor", size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {[4.5, 8, 11.5].map((r, i) => (
        <path
          key={r}
          d={`M${12 - r / 2} ${12 - r / 1.6} A ${r} ${r} 0 0 1 ${12 - r / 2} ${12 + r / 1.6}`}
          stroke={color}
          strokeWidth="1.8"
          strokeLinecap="round"
          opacity={1 - i * 0.28}
          transform={`translate(${i * 1.2} 0)`}
        />
      ))}
    </svg>
  );
}

// Cadre « foil » : dégradé métallisé dérivé de la couleur d'accent + coins renforcés.
// Variante « Grand luxe » : double cadre + ornements d'angle supplémentaires.
function FoilFrame({ accent, luxe = false }) {
  const gid = useId();
  const stops = [
    mix(accent, "#000000", 0.18),
    mix(accent, "#FFFFFF", 0.78),
    mix(accent, "#000000", 0.32),
    mix(accent, "#FFFFFF", 0.6),
    mix(accent, "#000000", 0.18),
  ];
  const corners = [
    "M15 36 V21 Q15 15 21 15 H36",
    "M136 15 H151 Q157 15 157 21 V36",
    "M157 72 V87 Q157 93 151 93 H136",
    "M36 93 H21 Q15 93 15 87 V72",
  ];
  // Volutes d'angle supplémentaires (Grand luxe)
  const flourishes = [
    "M22 30 q 6 -8 14 -8 q -8 -6 -14 -14 q 0 8 -6 14 q 8 0 6 8 Z",
    "M150 30 q -6 -8 -14 -8 q 8 -6 14 -14 q 0 8 6 14 q -8 0 -6 8 Z",
    "M22 78 q 6 8 14 8 q -8 6 -14 14 q 0 -8 -6 -14 q 8 0 6 -8 Z",
    "M150 78 q -6 8 -14 8 q 8 6 14 14 q 0 -8 6 -14 q -8 0 -6 -8 Z",
  ];
  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full"
      viewBox="0 0 172 108"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
          {stops.map((c, i) => (
            <stop key={i} offset={i / (stops.length - 1)} stopColor={c} />
          ))}
        </linearGradient>
      </defs>
      <rect x="8" y="8" width="156" height="92" rx="8" fill="none" stroke={`url(#${gid})`} strokeWidth="1.1" vectorEffect="non-scaling-stroke" opacity="0.95" />
      {luxe && (
        <rect x="11.5" y="11.5" width="149" height="85" rx="6.5" fill="none" stroke={`url(#${gid})`} strokeWidth="0.55" vectorEffect="non-scaling-stroke" opacity="0.75" />
      )}
      {corners.map((d) => (
        <path key={d} d={d} fill="none" stroke={`url(#${gid})`} strokeWidth="3" vectorEffect="non-scaling-stroke" strokeLinecap="round" />
      ))}
      {luxe && flourishes.map((d) => (
        <path key={d} d={d} fill={`url(#${gid})`} opacity="0.9" transform="translate(0 0)" />
      ))}
    </svg>
  );
}

// Filet orné d'un petit losange.
function Ornament({ color, width = "56%" }) {
  return (
    <div className="flex items-center" style={{ width, gap: "1.4cqw" }} aria-hidden="true">
      <span className="h-px flex-1" style={{ background: color, opacity: 0.65 }} />
      <span className="block rotate-45" style={{ background: color, width: "1.9cqw", height: "1.9cqw" }} />
      <span className="h-px flex-1" style={{ background: color, opacity: 0.65 }} />
    </div>
  );
}

function cardBgStyle(bg, pattern, patternColor, accent) {
  return pattern
    ? { backgroundImage: `${patternUri(pattern, patternColor || accent)}, ${bg}`, backgroundSize: "170px, cover" }
    : { background: bg };
}

// ——— RECTO premium ———
export default function CardMockup({
  name = "Votre Établissement",
  slogan = "",
  bg = "linear-gradient(135deg,#071A2D 0%,#0E2E52 100%)",
  fg = "#FFFFFF",
  accent = "#7FB0FF",
  logo = null,
  className = "",
  shine = false,
  pattern = null,
  patternColor = null,
  luxe = false,
}) {
  const monoColor = bg.includes("#F") || bg.includes("#DCE7F5") ? "#071A2D" : fg;
  return (
    <div className={`pvc-card ${className}`} style={{ ...cardBgStyle(bg, pattern, patternColor, accent), color: fg }} data-testid="card-mockup">
      <FoilFrame accent={accent} luxe={luxe} />

      {/* Contenu recto : logo / nom / ornement / slogan */}
      <div className="relative flex h-full flex-col items-center justify-center text-center" style={{ gap: "2.4cqw", padding: "0 12%" }}>
        {logo ? (
          <img src={logo} alt={`Logo ${name}`} className="object-contain" style={{ height: "15cqw", width: "15cqw", borderRadius: "2.5cqw" }} />
        ) : (
          <span
            className="flex items-center justify-center font-display font-bold"
            style={{
              height: "13cqw",
              width: "13cqw",
              borderRadius: "3cqw",
              background: accent,
              color: monoColor,
              fontSize: "6cqw",
            }}
          >
            {name.charAt(0).toUpperCase()}
          </span>
        )}
        <p className="font-display font-bold uppercase leading-tight" style={{ fontSize: "5.6cqw", letterSpacing: "0.14em" }}>
          {name}
        </p>
        <Ornament color={accent} />
        {slogan && (
          <p className="italic leading-snug" style={{ fontSize: "3cqw", opacity: 0.92 }}>
            {slogan}
          </p>
        )}
      </div>

      {/* NFC en haut à droite */}
      <div className="absolute flex flex-col items-center" style={{ top: "9%", right: "7%", gap: "0.6cqw" }}>
        <NfcWaves color={accent} size={18} />
        <span className="font-semibold uppercase" style={{ fontSize: "2.2cqw", letterSpacing: "0.22em", color: accent }}>NFC</span>
      </div>

      {/* Bas : mention */}
      <p className="absolute font-medium uppercase" style={{ left: "8%", right: "8%", bottom: "9%", fontSize: "2.4cqw", letterSpacing: "0.2em", opacity: 0.75 }}>
        Carte digitale professionnelle
      </p>

      <div className="pvc-glare" />
      {shine && <div className="pointer-events-none absolute inset-y-0 w-1/3 animate-shimmer bg-white/10" />}
    </div>
  );
}

// ——— VERSO premium complet ———
// Modèle officiel de la carte Noveocard : WhatsApp, Site internet, Avis Google, Instagram, TikTok.
const VERSO_ICONS = [
  [MessageSquare, "WhatsApp"],
  [Globe, "Site internet"],
  [Star, "Avis Google"],
  [Instagram, "Instagram"],
  [Music2, "TikTok"],
];

export function CardVerso({
  name = "Votre Établissement",
  bg = "linear-gradient(135deg,#071A2D 0%,#0E2E52 100%)",
  fg = "#FFFFFF",
  accent = "#7FB0FF",
  className = "",
  pattern = null,
  patternColor = null,
  luxe = false,
  slogan = "",
}) {
  return (
    <div className={`pvc-card ${className}`} style={{ ...cardBgStyle(bg, pattern, patternColor, accent), color: fg }} data-testid="card-verso">
      <FoilFrame accent={accent} luxe={luxe} />

      <div className="absolute flex flex-col items-center" style={{ top: "9%", right: "7%", gap: "0.6cqw" }}>
        <NfcWaves color={accent} size={18} />
        <span className="font-semibold uppercase" style={{ fontSize: "2.2cqw", letterSpacing: "0.22em", color: accent }}>NFC</span>
      </div>

      <div className="relative flex h-full flex-col items-center justify-center text-center" style={{ gap: "2.2cqw", padding: "4% 16% 17%" }}>
        {/* Geste téléphone */}
        <svg viewBox="0 0 60 40" style={{ height: "10cqw" }} fill="none" aria-hidden="true">
          <rect x="16" y="5" width="20" height="32" rx="4.5" stroke={fg} strokeWidth="3" />
          <line x1="23" y1="10" x2="29" y2="10" stroke={fg} strokeWidth="2.4" strokeLinecap="round" />
          <path d="M42 16 a 8 8 0 0 1 0 10" stroke={accent} strokeWidth="2.6" strokeLinecap="round" />
          <path d="M46 12 a 14 14 0 0 1 0 18" stroke={accent} strokeWidth="2.4" strokeLinecap="round" opacity="0.65" />
        </svg>
        <p className="font-display font-bold uppercase leading-tight" style={{ fontSize: "4.6cqw", letterSpacing: "0.24em" }}>
          Approchez votre téléphone
        </p>
        <Ornament color={accent} width="44%" />
        {slogan && (
          <p className="font-semibold uppercase" style={{ fontSize: "2.7cqw", letterSpacing: "0.3em", color: accent }}>
            {slogan}
          </p>
        )}

        {/* 6 accès */}
        <div className="flex w-full items-stretch justify-center" style={{ marginTop: "1.6cqw" }}>
          {VERSO_ICONS.map(([Icon, label], i) => (
            <div
              key={label}
              className="flex flex-col items-center"
              style={{
                gap: "1cqw",
                padding: "0 2.4cqw",
                borderLeft: i === 0 ? "none" : `1px solid ${fg}33`,
              }}
            >
              <Icon style={{ height: "4.4cqw", width: "4.4cqw", color: accent }} strokeWidth={1.8} />
              <span className="font-medium uppercase" style={{ fontSize: "1.9cqw", letterSpacing: "0.12em", opacity: 0.85 }}>
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Bas : marque */}
      <div className="absolute flex items-center" style={{ left: "14%", right: "14%", bottom: "8.5%", gap: "2.4cqw" }}>
        <span className="h-px flex-1" style={{ background: `${fg}55` }} />
        <span className="text-center">
          <span className="block font-display font-bold uppercase" style={{ fontSize: "3.2cqw", letterSpacing: "0.3em" }}>{name}</span>
          <span className="block" style={{ fontSize: "2.2cqw", letterSpacing: "0.14em", color: accent }}>
            {SITE_URL.replace("https://", "www.")}
          </span>
        </span>
        <span className="h-px flex-1" style={{ background: `${fg}55` }} />
      </div>

      <div className="pvc-glare" />
    </div>
  );
}

// ——— Mise en scène recto + verso, léger angle 3D + reflet ———
const REFLECT = { WebkitBoxReflect: "below 10px linear-gradient(transparent 62%, rgba(0,0,0,0.35))" };

export function CardPair({ className = "", children, ...cardProps }) {
  return (
    <div className={`flex items-center justify-center ${className}`} style={{ perspective: "1400px", gap: "5%" }}>
      <div className="w-[47%]" style={{ transform: "rotate(-4deg) rotateY(9deg)", ...REFLECT }}>
        <CardMockup {...cardProps} />
      </div>
      <div className="w-[47%]" style={{ transform: "rotate(3.5deg) rotateY(-9deg) translateY(2%)", ...REFLECT }}>
        <CardVerso {...cardProps} />
      </div>
    </div>
  );
}
